import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useLocations } from '../../context/LocationsContext'
import { LoadingPage } from '../ui/loading-spinner'

// Smart redirect component that checks business locations
const SmartRedirect = ({ user, location }) => {
  const { locations, loading } = useLocations()

  if (loading) {
    return <LoadingPage message="Checking business setup..." />
  }

  // If user has business locations, redirect to dashboard
  // If user doesn't have business locations, redirect to business setup
  const hasBusinessLocations = locations && locations.length > 0
  const redirectPath = hasBusinessLocations ? '/dashboard' : '/onboarding'
  const from = location.state?.from?.pathname || redirectPath
  
  return <Navigate to={from} replace />
}

const ProtectedRoute = ({ children, requireAuth = true, redirectTo = '/login' }) => {
  const { isAuthenticated, isLoading, user } = useAuth()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingPage message="Checking authentication..." />
  }

  // If route requires authentication but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location for redirecting after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // If route requires no authentication but user is authenticated (e.g., login page)
  if (!requireAuth && isAuthenticated) {
    // Check if user needs email verification first
    if (user && !user.email_verified) {
      return <Navigate to="/verify-email" replace />
    }
    // Use smart redirect to determine where to send the user
    return <SmartRedirect user={user} location={location} />
  }

  // If user is authenticated but email is not verified
  if (requireAuth && isAuthenticated && user && !user.email_verified) {
    return <Navigate to="/verify-email" replace />
  }

  // If user is authenticated but hasn't completed onboarding
  if (requireAuth && isAuthenticated && user && user.email_verified && !user.onboarding_completed) {
    // Allow access to onboarding page
    if (location.pathname === '/onboarding') {
      return children
    }
    // Redirect to onboarding for all other protected routes
    return <Navigate to="/onboarding" replace />
  }

  // If user has completed onboarding but is trying to access onboarding page
  if (requireAuth && isAuthenticated && user && user.onboarding_completed && location.pathname === '/onboarding') {
    return <SmartRedirect user={user} location={location} />
  }

  return children
}

// Higher-order component for protecting routes
export const withAuth = (Component, options = {}) => {
  return function AuthenticatedComponent(props) {
    return (
      <ProtectedRoute {...options}>
        <Component {...props} />
      </ProtectedRoute>
    )
  }
}

// Component for routes that should only be accessible to unauthenticated users
export const PublicOnlyRoute = ({ children }) => {
  const { user, isAuthenticated } = useAuth()
  const location = useLocation()
  
  if (isAuthenticated && user) {
    return <SmartRedirect user={user} location={location} />
  }
  
  return children
}

// Component for admin-only routes
export const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <LoadingPage message="Checking permissions..." />
  }

  if (!isAuthenticated || !user || user.role !== 'admin') {
    return <SmartRedirect user={user} location={location} />
  }

  return children
}

export default ProtectedRoute
